import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../component/header";
import Footer from "../component/Footer";
import Breadcrumb from "../component/Breadcrumb";
import { ArrowLeft, ArrowRight, BookOpen, CheckCircle2, ExternalLink, Link as LinkIcon, Sparkles } from "lucide-react";
import { fetchRefinePage, findDefaultRefinePage } from "../utilis/refinePages";

const URL_PATTERN = /(https?:\/\/[^\s\]]+)/g;
const URL_ONLY_PATTERN = /^https?:\/\/[^\s\]]+$/;

const renderLinkedText = (value = "") =>
  String(value)
    .split(URL_PATTERN)
    .map((part, index) => {
      if (!URL_ONLY_PATTERN.test(part)) return part;
      return (
        <a
          key={`${part}-${index}`}
          href={part}
          target="_blank"
          rel="noreferrer"
          className="font-semibold text-[#007D91] underline decoration-[#00C3FF]/50 underline-offset-4 transition hover:text-[#005F70]"
        >
          {part}
        </a>
      );
    });

function RefineResourceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [resource, setResource] = useState(() => findDefaultRefinePage(slug));
  const [loading, setLoading] = useState(true);
  const hasDocumentFlow = resource?.documentFlow?.length > 0;

  useEffect(() => {
    let active = true;
    setLoading(true);

    fetchRefinePage(slug)
      .then((page) => {
        if (active) setResource(page);
      })
      .catch((error) => {
        if (!active) return;
        setResource(error.status === 404 ? null : findDefaultRefinePage(slug));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [slug]);

  if (!loading && !resource) {
    return (
      <div className="App min-h-screen bg-[#F9FAFB]">
        <Header />
        <main className="mx-auto max-w-4xl px-4 py-32 text-center">
          <h1 className="font-cinzel text-4xl text-[#111718]">Resource not found</h1>
          <p className="mt-3 font-outfit text-[#5f6c6d]">This Refine resource is not available.</p>
          <Link to="/refine" className="mt-8 inline-flex items-center gap-2 rounded-full bg-black px-5 py-3 font-outfit text-sm font-bold text-white">
            <ArrowLeft size={16} />
            Back to Refine
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (loading && !resource) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <div className="h-12 w-12 rounded-full border-4 border-[#00C3FF] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="App min-h-screen bg-[#F9FAFB] text-left">
      <Header />
      <main>
        <section className="relative overflow-hidden bg-[#101819] px-4 pb-16 pt-32 text-white">
          <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[1fr_420px] lg:items-end">
            <div>
              <button
                type="button"
                onClick={() => navigate("/refine")}
                className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 font-outfit text-sm text-white/85 hover:border-[#00F0FB] hover:text-[#00F0FB]"
              >
                <ArrowLeft size={16} />
                Back to Refine
              </button>
              <p className="font-outfit text-xs font-bold uppercase tracking-[0.28em] text-[#00F0FB]">
                Refine Education
              </p>
              <h1 className="mt-4 font-cinzel text-4xl leading-tight md:text-6xl">{resource.title}</h1>
              <p className="mt-5 max-w-3xl font-outfit text-base leading-8 text-white/80">
                {resource.intro || resource.detail}
              </p>
            </div>
            <div className="overflow-hidden rounded-2xl border border-white/15 bg-white/10 p-3">
              <img src={resource.image} alt={resource.title} className="h-[330px] w-full rounded-xl object-cover" />
            </div>
          </div>
        </section>

        <Breadcrumb items={[{ label: "Education", to: "/education" }, { label: "Refine", to: "/refine" }, { label: resource.title }]} />

        <section className="mx-auto max-w-6xl px-4 py-14">
          <div className="grid gap-6 lg:grid-cols-[0.78fr_1.22fr]">
            <aside className="self-start rounded-2xl border border-[#dce5e5] bg-white p-6 shadow-sm lg:sticky lg:top-24">
              <BookOpen className="text-[#008EA8]" size={28} />
              <h2 className="mt-4 font-cinzel text-3xl text-[#111718]">Resource Overview</h2>
              <p className="mt-4 font-outfit text-sm leading-7 text-[#536061]">
                {resource.detail}
              </p>
              <div className="mt-6 rounded-xl bg-[#F4FBFC] p-4">
                <p className="font-outfit text-xs font-bold uppercase tracking-[0.18em] text-[#008EA8]">
                  Page focus
                </p>
                <p className="mt-2 font-outfit text-sm leading-6 text-[#394748]">
                  This page covers {resource.title.toLowerCase()} only, with focused guidance and reference links for that area.
                </p>
              </div>
              <Link
                to="/refine"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#00F0FB] px-5 py-3 font-outfit text-sm font-bold text-black"
              >
                Explore more resources <ArrowRight size={16} />
              </Link>
            </aside>

            <div className="space-y-6">
              <div className="rounded-2xl border border-[#dce5e5] bg-white p-6 shadow-sm">
                <h2 className="font-cinzel text-3xl text-[#111718]">
                  {hasDocumentFlow ? "Technical Skills Flow" : "What you will refine"}
                </h2>
                <p className="mt-3 font-outfit text-sm leading-7 text-[#536061]">
                  {hasDocumentFlow
                    ? "This path is organized as a clear learning guide: intro, brief, prompt, examples, guidance, review criteria, resources, and standards."
                    : "Work through the sections in order, then use the links at the bottom when you need deeper reference material."}
                </p>
              </div>

              {hasDocumentFlow && resource.documentFlow.map((section, index) => (
                <article
                  key={section.title}
                  className="rounded-2xl border border-[#dce5e5] bg-white p-6 shadow-sm"
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#E8FBFD] font-outfit text-sm font-bold text-[#008EA8]">
                      {index + 1}
                    </span>
                    <p className="font-outfit text-xs font-bold uppercase tracking-[0.18em] text-[#008EA8]">
                      {section.label}
                    </p>
                  </div>
                  <h3 className="mt-4 font-cinzel text-2xl text-[#111718]">{section.title}</h3>
                  {section.body && (
                    <p className="mt-3 font-outfit text-sm leading-7 text-[#536061]">{renderLinkedText(section.body)}</p>
                  )}
                  {section.items?.length > 0 && (
                    <div className="mt-5 grid gap-3">
                      {section.items.map((item) => (
                        <div key={item} className="flex items-start gap-3 rounded-xl bg-[#F4FBFC] p-4">
                          <Sparkles className="mt-0.5 flex-shrink-0 text-[#008EA8]" size={18} />
                          <p className="font-outfit text-sm leading-6 text-[#394748]">{renderLinkedText(item)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </article>
              ))}

              {resource.sections?.map((section) => (
                <article key={section.title} className="rounded-2xl border border-[#dce5e5] bg-white p-6 shadow-sm">
                  <h3 className="font-cinzel text-2xl text-[#111718]">{section.title}</h3>
                  <p className="mt-2 font-outfit text-sm leading-7 text-[#536061]">{section.description}</p>
                  <div className="mt-5 grid gap-3">
                    {section.items.map((item) => (
                      <div key={item} className="flex items-start gap-3 rounded-xl bg-[#F4FBFC] p-4">
                        <CheckCircle2 className="mt-0.5 flex-shrink-0 text-[#008EA8]" size={18} />
                        <p className="font-outfit text-sm leading-6 text-[#394748]">{renderLinkedText(item)}</p>
                      </div>
                    ))}
                  </div>
                </article>
              ))}

              {resource.externalLinks?.length > 0 && (
                <section className="rounded-2xl border border-[#dce5e5] bg-white p-6 shadow-sm">
                  <h2 className="font-cinzel text-3xl text-[#111718]">Helpful Links</h2>
                  <p className="mt-3 font-outfit text-sm leading-7 text-[#536061]">
                    Open these resources when you want deeper reference material or official guidance.
                  </p>
                  <div className="mt-6 grid gap-4 md:grid-cols-2">
                    {resource.externalLinks.map((link) => {
                      const isInternal = link.url.startsWith("/");
                      const content = (
                        <>
                          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#E8FBFD] text-[#008EA8]">
                            {isInternal ? <LinkIcon size={18} /> : <ExternalLink size={18} />}
                          </span>
                          <span>
                            <span className="block font-outfit text-base font-bold text-[#111718]">{link.title}</span>
                            <span className="mt-1 block font-outfit text-sm leading-6 text-[#536061]">{link.description}</span>
                          </span>
                        </>
                      );

                      return isInternal ? (
                        <Link
                          key={link.title}
                          to={link.url}
                          className="flex gap-3 rounded-xl border border-[#dce5e5] p-4 transition hover:border-[#00C8D8] hover:bg-[#F4FBFC]"
                        >
                          {content}
                        </Link>
                      ) : (
                        <a
                          key={link.title}
                          href={link.url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex gap-3 rounded-xl border border-[#dce5e5] p-4 transition hover:border-[#00C8D8] hover:bg-[#F4FBFC]"
                        >
                          {content}
                        </a>
                      );
                    })}
                  </div>
                </section>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default RefineResourceDetail;
